<template>
  <div
    class="vc-section vc-card vc-card--compact vc-little-assistant"
    :class="{ 'vc-little-assistant--collapsed': !isAssistantEnabled }"
  >
    <div class="vc-little-assistant__head">
      <span class="vc-little-assistant__title">语音助手拦截</span>
      <div class="vc-little-assistant__switch">
        <span class="vc-little-assistant__switch-label">{{
          isAssistantEnabled ? '开启' : '关闭'
        }}</span>
        <i-switch v-model="assistant.enabled" />
      </div>
    </div>

    <div v-show="isAssistantEnabled" class="vc-little-assistant__body">
      <div class="assistant-field">
        <div class="assistant-field__label">意图词库</div>
        <Select
          ref="intentLexiconSelect"
          v-model="assistant.intentLexiconId"
          filterable
          clearable
          events-enabled
          placeholder="请选择意图词库"
          class="assistant-field__select"
          @click.native.stop
          @on-open-change="onIntentLexiconSelectOpenChange"
        >
          <Option
            v-for="item in intentLexiconOptions"
            :key="item.label_id"
            :value="item.label_id"
          >
            {{ formatLexiconOptionLabel(item) }}
          </Option>
        </Select>
      </div>

      <div class="assistant-field assistant-field--last">
        <div class="assistant-field__label">关键词</div>
        <KeywordTagEditor v-model="assistant.sentences" :disabled="false" />
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { getSystemIntentA } from '@/server/api'
import KeywordTagEditor from '../shared/KeywordTagEditor.vue'

/** 小助理意图词库仅展示该 label 的项（common + special） */
const ASSISTANT_INTENT_LEXICON_LABEL = '语音助手'

export default {
  name: 'LlmLittleAssistantSection',
  components: { KeywordTagEditor },
  props: {
    assistant: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      intentLexiconOptions: [],
      intentLexiconScrollCloseHandlers: [],
    }
  },
  computed: {
    ...mapState('scene', ['industry']),
    isAssistantEnabled() {
      return !!this.assistant.enabled
    },
  },
  watch: {
    industry: {
      handler(val) {
        if (val && (val.industry != null || val.scene != null)) {
          this.fetchIntentLexiconOptions()
        }
      },
      deep: true,
      immediate: true,
    },
  },
  beforeDestroy() {
    this.detachIntentLexiconScrollClose()
  },
  methods: {
    formatLexiconOptionLabel(item) {
      if (!item) return ''
      return `${item.own !== 0 ? '个性库－' : ''}${item.industryName || ''}${
        item.sceneName ? `-${item.sceneName}` : ''
      }${item.label ? `-${item.label}` : ''}${
        item.describe ? `(描述:${item.describe})` : ''
      }`
    },
    fetchIntentLexiconOptions() {
      const industry = this.industry && this.industry.industry
      const scene = this.industry && this.industry.scene
      if (industry == null && scene == null) return
      getSystemIntentA({
        industry,
        scene,
      }).then((res) => {
        const { common = [], special = [] } = res.data || {}
        const matchLabel = (item) => item && item.label === ASSISTANT_INTENT_LEXICON_LABEL
        this.intentLexiconOptions = [...common, ...special].filter(matchLabel)
        this.syncIntentLexiconSelection()
      })
    },
    syncIntentLexiconSelection() {
      const currentId =
        this.assistant.intentLexiconId != null ? String(this.assistant.intentLexiconId) : ''
      if (!currentId) return
      const valid = this.intentLexiconOptions.some(
        (item) => String(item.label_id) === currentId,
      )
      if (!valid) {
        this.assistant.intentLexiconId = ''
      }
    },
    onIntentLexiconSelectOpenChange(open) {
      if (open) {
        this.attachIntentLexiconScrollClose()
      } else {
        this.detachIntentLexiconScrollClose()
      }
    },
    attachIntentLexiconScrollClose() {
      this.detachIntentLexiconScrollClose()
      const scrollRoots = []
      if (this.$el && this.$el.closest) {
        const tabScroll = this.$el.closest('.llm-tab-pane-scroll')
        if (tabScroll) scrollRoots.push(tabScroll)
      }
      if (!scrollRoots.length) return

      const closeSelect = () => {
        const select = this.$refs.intentLexiconSelect
        if (select && select.visible && typeof select.hideMenu === 'function') {
          select.hideMenu()
        }
      }

      scrollRoots.forEach((el) => {
        el.addEventListener('scroll', closeSelect, { passive: true })
        this.intentLexiconScrollCloseHandlers.push({ el, closeSelect })
      })
    },
    detachIntentLexiconScrollClose() {
      this.intentLexiconScrollCloseHandlers.forEach(({ el, closeSelect }) => {
        el.removeEventListener('scroll', closeSelect)
      })
      this.intentLexiconScrollCloseHandlers = []
    },
  },
}
</script>

<style lang="scss" scoped>
@import '../voice-call/shared.scss';

.vc-section {
  margin-bottom: 16px;
}

.vc-card {
  background: #fff;
  border: 1px solid #e8eaec;
  border-radius: 8px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  padding: 20px;
}

.vc-card--compact {
  padding-bottom: 16px;
}

.vc-little-assistant {
  text-align: left;
}

.vc-little-assistant--collapsed {
  padding-bottom: 20px;

  .vc-little-assistant__head {
    margin-bottom: 0;
  }
}

.vc-little-assistant__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  min-height: 24px;
}

.vc-little-assistant__title {
  font-size: 15px;
  font-weight: 600;
  color: #262626;
  line-height: 24px;
}

.vc-little-assistant__switch {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.vc-little-assistant__switch-label {
  font-size: 13px;
  color: #595959;
  line-height: 24px;
}

.vc-little-assistant__body {
  padding-top: 0;
}

.assistant-field {
  margin-bottom: 16px;
  min-width: 0;

  &--last {
    margin-bottom: 0;
  }

  &__label {
    margin-bottom: 8px;
    font-size: 13px;
    font-weight: 500;
    color: #262626;
    line-height: 1.5;
    text-align: left;
  }

  &__select {
    width: 100%;

    ::v-deep .ivu-select-selection,
    ::v-deep .ivu-select-selected-value,
    ::v-deep .ivu-select-input,
    ::v-deep .ivu-select-placeholder {
      text-align: left;
    }

    ::v-deep .ivu-select-dropdown,
    ::v-deep .ivu-select-item {
      text-align: left;
    }
  }
}
</style>
